import { useRef, useState } from 'react';
import type { ClipboardEvent, KeyboardEvent } from 'react';
import { ERRORS } from '@/constants';
import { ApiError } from '@/services/api/client';
import { useVerifyMfa } from '@/services/queries';
import { Button } from '@/shared/ui/button';
import type { MfaChallenge } from '@/types';
import { AuthenticatorSetup } from './AuthenticatorSetup';

const LENGTH = 6;

/**
 * The second step of signing in, for an account with two-factor on.
 *
 * Six boxes rather than one input, as in the admin console: the code is read
 * off a phone in pairs, and a paste of all six fills them and submits. The
 * heading lives in the page (it swaps with the step), so this is only the form.
 *
 * A first login on an account provisioned with a secret arrives with `setup`,
 * and shows the QR code above the boxes - the screen asking for the code is the
 * one that supplies it.
 */
export function MfaForm({
  challenge,
  onExpired,
  onBack,
}: {
  challenge: MfaChallenge;
  onExpired: () => void;
  onBack: () => void;
}) {
  const verify = useVerifyMfa();
  const [digits, setDigits] = useState<string[]>(() => Array<string>(LENGTH).fill(''));
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const code = digits.join('');

  const submit = (value: string) => {
    if (value.length !== LENGTH) return;

    verify.mutate(
      { challengeId: challenge.challengeId, code: value },
      {
        onError: (error) => {
          setDigits(Array<string>(LENGTH).fill(''));
          inputs.current[0]?.focus();
          if (error instanceof ApiError && error.code === 'mfa_challenge_expired') onExpired();
        },
      },
    );
  };

  const setDigit = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    if (!digit && raw !== '') return;

    const next = [...digits];
    next[index] = digit;
    setDigits(next);

    if (digit && index < LENGTH - 1) inputs.current[index + 1]?.focus();
    if (digit && index === LENGTH - 1) submit(next.join(''));
  };

  const onKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      inputs.current[index - 1]?.focus();
      const next = [...digits];
      next[index - 1] = '';
      setDigits(next);
    }
    if (event.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
    if (event.key === 'ArrowRight' && index < LENGTH - 1) inputs.current[index + 1]?.focus();
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH);
    if (!pasted) return;

    event.preventDefault();
    const next = Array<string>(LENGTH).fill('');
    for (const [i, char] of [...pasted].entries()) next[i] = char;
    setDigits(next);

    inputs.current[Math.min(pasted.length, LENGTH - 1)]?.focus();
    if (pasted.length === LENGTH) submit(pasted);
  };

  const apiError = verify.error instanceof ApiError ? verify.error : null;

  const banner = (): string | null => {
    if (!apiError) return null;
    if (apiError.code === 'mfa_invalid') return ERRORS.mfaInvalid;
    if (apiError.code === 'mfa_challenge_expired') return ERRORS.mfaExpired;
    if (apiError.code === 'rate_limited') return ERRORS.rateLimited;
    if (apiError.code === 'account_locked') return ERRORS.accountLocked;
    return apiError.message;
  };

  const message = banner();
  const setup = challenge.setup;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit(code);
      }}
      className="space-y-4"
      noValidate
    >
      <p className="text-sm text-muted-foreground">
        {setup
          ? 'Scan this with your authenticator app, then enter the code it shows to confirm.'
          : `Enter the ${String(LENGTH)}-digit code from your authenticator app. Codes rotate every 30 seconds.`}
      </p>

      {message && (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {message}
        </p>
      )}

      {setup && (
        <AuthenticatorSetup
          secret={setup.secret}
          otpauthUrl={setup.otpauthUrl}
          lostSecretHint="Shown once. If the code below never confirms it, ask an administrator to reset your second factor."
        />
      )}

      <div
        role="group"
        aria-label={`${String(LENGTH)}-digit verification code`}
        className="flex gap-2.5"
      >
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(element) => {
              inputs.current[index] = element;
            }}
            value={digit}
            onChange={(event) => {
              setDigit(index, event.target.value);
            }}
            onKeyDown={onKeyDown(index)}
            onPaste={onPaste}
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            autoFocus={index === 0}
            disabled={verify.isPending}
            aria-label={`Digit ${String(index + 1)}`}
            className="h-12 w-full rounded-[9px] border-[1.5px] border-input bg-background text-center font-mono text-xl font-semibold text-primary shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
          />
        ))}
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={verify.isPending || code.length !== LENGTH}
      >
        {verify.isPending ? 'Verifying…' : 'Verify and continue'}
      </Button>

      <button
        type="button"
        onClick={onBack}
        className="text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        ← Back to sign in
      </button>
    </form>
  );
}
